import * as Sentry from "@sentry/vue";
import { s3Upload } from "@/api/api";

/**
 * Orquesta la subida directa a S3 de un archivo de import
 * (decisiones §35): la comparten el mayor y ventas/costos.
 *
 *   1. `getUploadUrl()` → {data: {url, fields, name}} (formulario prefirmado).
 *   2. POST al bucket con fields + file (`s3Upload`: sin JWT, con % de progreso).
 *   3. `confirm(name)` → 202: el backend valida la referencia y encola.
 *
 * Fallback a `multipart(onUploadProgress)` (el archivo viaja por el web dyno,
 * camino de siempre) si el ambiente no tiene S3 (503 en el paso 1) o si S3
 * rechaza el POST sin haber recibido bytes (CORS del bucket, firma inválida):
 * mientras el multipart exista, ningún analista se queda sin subir. Un corte
 * de red con bytes ya enviados NO cae al multipart (reenviar el archivo entero
 * por el camino que el router corta a 30 s no lo salvaría): se propaga.
 *
 * @param {Object} options
 * @param {() => Promise} options.getUploadUrl
 * @param {(name: string) => Promise} options.confirm
 * @param {(onUploadProgress?: Function) => Promise} options.multipart
 * @param {File} options.file
 * @param {Function} [options.onUploadProgress] - Callback axios {loaded, total}.
 * @param {string} [options.label] - Nombre del flujo para el rastro en Sentry.
 * @returns {Promise} La respuesta del paso 3 (o del multipart).
 */
export async function directUpload({
	getUploadUrl,
	confirm,
	multipart,
	file,
	onUploadProgress,
	label = "import",
}) {
	let signed;
	try {
		({ data: signed } = await getUploadUrl());
	} catch (error) {
		if (error.response && error.response.status === 503) {
			return multipart(onUploadProgress);
		}
		throw error;
	}
	// ¿Llegó a salir algún byte hacia S3? Distingue el rechazo inmediato
	// (CORS/preflight, firma inválida — el navegador no reporta progreso) de
	// una conexión que se cortó a mitad de un archivo grande.
	let bytesSent = 0;
	const trackProgress = (event) => {
		bytesSent = Math.max(bytesSent, (event && event.loaded) || 0);
		if (onUploadProgress) {
			onUploadProgress(event);
		}
	};
	try {
		await s3Upload(signed.url, signed.fields, file, trackProgress);
	} catch (error) {
		const s3Answered = !!error.response;
		if (!s3Answered && bytesSent > 0) {
			throw error;
		}
		// S3 rechazó el formulario (política/firma) o el bucket no acepta el
		// origen (CORS): el multipart sigue vivo (decisiones §35, decisión 5). Rastro en
		// Sentry para saber cuándo retirarlo.
		Sentry.captureMessage(
			`Subida directa a S3 rechazada (${label}), fallback a multipart: ${
				(error.response && error.response.status) || error.message
			}`,
			"warning"
		);
		if (onUploadProgress) {
			// La barra vuelve a 0: empieza otra transferencia.
			onUploadProgress({ loaded: 0, total: file.size });
		}
		return multipart(onUploadProgress);
	}
	return confirm(signed.name);
}
